// @flow

import * as React from 'react';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';

import ButtonAction from './ButtonAction';

import type { CommonActionProps } from './types.actions';
import { DesktopView, MobileView } from '../../config/mediaQuery';

type GridSelection = {
  row: Int,
};

type GridRow = {
  level: Int,
  hasChildren: boolean,
  isCollapsed: boolean,
};

type Grid = {
  collapseGroupsToLevel: () => void,
  beginUpdate: () => void,
  endUpdate: () => void,
  selection: GridSelection,
  rows: Array<GridRow>,
};

type Props = {
  grid: Grid,
  disabled?: boolean,
  onGroupCollapsedChanged: () => void,
  setCollapseLoading?: (isLoading: boolean) => void,
};

type State = {};

class CollapseAction extends React.Component<Props & CommonActionProps, State> {
  static defaultProps = {
    size: 'xsmall',
    disabled: false,
  };

  componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>, snapshot: SS) {
    if (this.props.grid !== prevProps.grid) {
      this.props.grid.rows.forEach(row => {
        row.isCollapsed = false;
      });
      this.props.onGroupCollapsedChanged();
    }
  }

  handleCollapse = () => {
    const { grid, onGroupCollapsedChanged, setCollapseLoading } = this.props;

    // Show overlay before any blocking operation
    if (setCollapseLoading) {
      setCollapseLoading(true);
    }

    // Use requestAnimationFrame + setTimeout to ensure the browser has time to repaint
    // and show the BlockingStatus overlay before the blocking operation begins
    requestAnimationFrame(() => {
      setTimeout(() => {
        // No row or group selected - collapse all to level 0
        if (grid.selection.row === -1) {
          grid.collapseGroupsToLevel(0);
          onGroupCollapsedChanged();
          this.hideOverlay();
          return;
        }

        const rowSelected = grid.rows[grid.selection.row];
        const rowLevel = rowSelected.level;

        // Selected row is not a group - collapse all to level 0
        if (!rowSelected.hasChildren) {
          grid.collapseGroupsToLevel(0);
          onGroupCollapsedChanged();
          this.hideOverlay();
          return;
        }

        // Group row selected - collapse all groups at the same level
        // Batch DOM updates for better performance on large grids
        grid.beginUpdate();
        try {
          // Iterate through ALL rows (from index 0), not just from selected row forward.
          // This ensures all group rows at the same level are collapsed, regardless of
          // whether they appear before or after the selected row in the grid.
          for (let i = 0; i < grid.rows.length; i++) {
            const currentRow = grid.rows[i];
            if (currentRow.hasChildren && currentRow.level === rowLevel) {
              currentRow.isCollapsed = true;
            }
          }
        } finally {
          grid.endUpdate();
        }

        onGroupCollapsedChanged();
        this.hideOverlay();
      }, 50);
    });
  };

  hideOverlay = () => {
    const { setCollapseLoading } = this.props;
    if (setCollapseLoading) {
      // Use requestAnimationFrame to ensure the grid's visual update is painted
      // before removing the overlay
      requestAnimationFrame(() => {
        setCollapseLoading(false);
      });
    }
  };

  render() {
    const { size, disabled } = this.props;

    return (
      <>
        <DesktopView>
          <ButtonAction
            onClick={this.handleCollapse}
            bsSize={size}
            disabled={disabled}
          >
            <Glyphicon glyph="collapse-up" /> Collapse
          </ButtonAction>
        </DesktopView>
        <MobileView>
          <button
            className='collapse-menu-button'
            onClick={this.handleCollapse}
            disabled={disabled}
          >
            <img src='assets/collapse.svg' alt='collapse icon'/>Collapse
          </button>
        </MobileView>
      </>
    );
  }
}

export default CollapseAction;
